import { CampaignImage } from "@/components/campaign-card/campaign-image";
import { CampaignCountdown } from "@/components/campaign-card/campaign-countdown";
import { useWidgetVariant } from "@/contexts/widget-variant";
import type { CampaignSchema } from "@/types";
import {
  startTransition,
  useRef,
  useState,
  type ComponentProps,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { RichText } from "@/components/ui/rich-text";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { GamesPill } from "@/components/campaigns/games-pill";
import {
  CHIP_FILL,
  CHIP_RIM,
  GradientRim,
  PILL_FILL,
  PILL_RIM,
  TRACK_FILL,
  TRACK_RIM,
} from "@/components/ui/glass";
import { useAppStore } from "@/stores";
import { CARD_BODY_TEXT } from "./card-body-text";
import { formatMoney } from "@/utils/format-money";
import { useTranslation } from "react-i18next";

/** The three tabs shared by every campaign type. Only the primary tab's label
 *  and content vary by type; Info and T&C are identical everywhere. */
export type CampaignTab = "primary" | "info" | "terms";

export const SectionLabel = ({
  children,
  className,
  ...props
}: {
  children: ReactNode;
  className?: string;
} & Omit<ComponentProps<"p">, "children" | "className">) => (
  <p
    {...props}
    className={cn(
      "font-heading text-[10px] leading-[13px] font-semibold tracking-[0.8px] text-muted-foreground uppercase lg:text-[11px] lg:leading-[14px] lg:tracking-[0.88px]",
      className
    )}
  >
    {children}
  </p>
);

/** A card's muted footnote: the one line under its content that qualifies it ("Earn 120 pts to
 *  enter the prize zone", a race's spin cap). */
export const CardNote = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <p
    className={cn(
      "w-full text-left font-body text-[12px] leading-4 text-muted-foreground",
      className
    )}
  >
    {children}
  </p>
);

/** Campaign banner card — the top of the sidebar, identical for every type.
 *  Finished campaigns desaturate the artwork and overlay a "Finished" badge. */
export const CampaignBanner = ({
  campaign,
  finished = false,
}: {
  campaign: CampaignSchema;
  finished?: boolean;
}) => (
  <Card inset="none">
    <div className="aspect-[338/161] w-full overflow-hidden">
      <CampaignImage
        campaign={campaign}
        hideTitle
        hideDuration
        className={cn(finished && "opacity-70 grayscale")}
      />
    </div>
    <div className="h-px w-full bg-foreground/[0.06]" />
  </Card>
);

/**
 * "Min. bet €4" pill — the qualifying threshold matters enough to players
 * (bets under it silently don't count) that it gets the filled-pill emphasis
 * of the tournament points chip rather than a muted text label. Shared by
 * every card that surfaces a minimum bet.
 */
export const MinBetPill = ({
  minimumBet,
  appearance = "pill",
}: {
  minimumBet?: { value: number; currency: string; display?: string };
  /** `text`: a plain muted line, for a footer that only notes the threshold
   *  (the overlay's dock panel) rather than giving it the pill's emphasis. */
  appearance?: "pill" | "text";
}) => {
  const { t } = useTranslation();
  if (minimumBet == null || minimumBet.value <= 0) return null;
  if (appearance === "text") {
    return (
      <span>
        {t("campaign.minBet", "Min. bet")}{" "}
        {minimumBet.display ?? formatMoney(minimumBet)}
      </span>
    );
  }
  return (
    <Badge
      variant="tonal"
      color="primary"
      // A notch under the badge's 11px on phones: uppercase + tracking makes the label read
      // bigger than it is next to the 11px section heading it shares the row with.
      className="text-[10px] font-normal tracking-[0.5px] uppercase lg:text-[12px]"
    >
      {t("campaign.minBet", "Min. bet")}{" "}
      {/* Prefer the backend's canonical, locale-correct string (matches
          overlay/T&C exactly); fall back to client formatting only if absent. */}
      {minimumBet.display ?? formatMoney(minimumBet)}
    </Badge>
  );
};

/** A card's headline number ("60,018" prizes left, "2 of 6" completed): the prize gold in the
 *  heading face, on one line. */
export const StatFigure = ({
  density = "default",
  children,
}: {
  /** `dock`: a notch smaller for the overlay's narrow column. */
  density?: "default" | "dock";
  children: ReactNode;
}) => (
  <p
    className={cn(
      "font-heading font-semibold whitespace-nowrap text-prizes",
      density === "dock"
        ? "text-3xl leading-none tracking-tight"
        : "text-[36px] leading-9 tracking-[-0.72px]"
    )}
  >
    {children}
  </p>
);

/** Short "how to win" text card in the sidebar (per-type copy). */
export const HowToWinCard = ({
  title,
  text,
  minimumBet,
}: {
  title?: string;
  text: string;
  minimumBet?: { value: number; currency: string; display?: string };
}) => {
  const { t } = useTranslation();
  return (
    <Card
      title={title ?? t("campaign.howToWin", "How to win")}
      aside={<MinBetPill minimumBet={minimumBet} />}
    >
      <p className={cn("w-full font-body", CARD_BODY_TEXT)}>{text}</p>
    </Card>
  );
};

/**
 * The "How it works" sidebar card every type explains itself with — the same surface as
 * `HowToWinCard`, under the one shared heading (`campaign.howItWorksHeading`, translated once
 * for every type) so a new type never ships its own copy of the label. Only the body text is
 * per type.
 */
export const HowItWorksCard = ({
  text,
  minimumBet,
}: {
  text: string;
  minimumBet?: { value: number; currency: string; display?: string };
}) => {
  const { t } = useTranslation();
  return (
    <HowToWinCard
      title={t("campaign.howItWorksHeading", "How it works")}
      text={text}
      minimumBet={minimumBet}
    />
  );
};

/**
 * Wraps an Info-tab section in a card: the sections stack straight on the page on every layout
 * (Figma 5129:5966), so each needs its own surface. Pass a `title` to give the section a header
 * (like Eligible games).
 */
export const InfoSection = ({
  title,
  aside,
  children,
  className,
}: {
  title?: string;
  /** Sits at the far end of the title row (e.g. the time-left chip on About). */
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
}) => (
  <Card title={title} aside={aside} className={cn("text-left", className)}>
    {children}
  </Card>
);

/**
 * Small glass "Ends in 9 hrs 36 min" chip for the in-game phone layout, where the cover wears
 * nothing but the close and the header has no room for a clock: it sits across from "About" in
 * the Info tab. Gates itself — on-site the banner carries the time, desktop has the header clock —
 * so the modules can pass it unconditionally.
 */
export const CampaignTimeLeftChip = ({
  campaign,
  className,
}: {
  campaign: CampaignSchema;
  className?: string;
}) => {
  const inGame = useWidgetVariant() === "in-game";
  if (!inGame) return null;
  return (
    <div
      className={cn(
        "relative flex h-6 shrink-0 items-center overflow-hidden rounded-full px-2 text-foreground lg:hidden ls:hidden",
        className
      )}
      style={{ background: CHIP_FILL }}
    >
      <GradientRim background={CHIP_RIM} />
      <CampaignCountdown
        campaign={campaign}
        variant="inline"
        className="relative text-[11px] [&_svg]:size-3"
      />
    </div>
  );
};

export const CampaignDescriptionText = ({ text }: { text?: string }) => (
  <RichText body={text} className={CARD_BODY_TEXT} />
);

/**
 * The Info tab's "Eligible games" section: the heading — with the game count when the list is
 * a subset — goes through the Card title like every other card heading; the body is the games
 * pill (or "All games"). Renders nothing for a campaign with no eligible games at all.
 */
export const EligibleGamesSection = ({
  games,
  count,
  campaignId,
  className,
}: {
  games: string[] | "ALL";
  count?: number;
  /** Lets an "All games" row open the dialog: its names are paged from the server per campaign. */
  campaignId: number;
  className?: string;
}) => {
  const { t } = useTranslation();
  if (games !== "ALL" && games.length === 0) return null;
  const total = games === "ALL" ? null : (count ?? games.length);
  return (
    <InfoSection
      title={t("eligibleGames.heading", "Eligible games")}
      // The count as a chip across from the heading, like the time left on About.
      aside={
        total !== null && (
          <Badge variant="ghost" color="neutral">
            {total.toLocaleString()}
          </Badge>
        )
      }
      className={className}
    >
      <EligibleGamesChips games={games} count={count} campaignId={campaignId} />
    </InfoSection>
  );
};

export const EligibleGamesChips = ({
  games,
  count,
  campaignId,
}: {
  games: string[] | "ALL";
  count?: number;
  campaignId: number;
}) => {
  const { t } = useTranslation();
  const isProvider = useAppStore((s) => s.isProvider);

  if (games === "ALL") {
    // The names are paged from the server (an "All games" selection ships none). A game
    // provider's "all games" is its own catalogue, so the pill names the size of it ("1,234
    // games") whenever the count came with the campaign; otherwise it says "All games".
    return (
      <GamesPill
        campaignId={campaignId}
        count={count}
        label={
          isProvider && count != null
            ? undefined
            : t("eligibleGames.allGames", "All games")
        }
      />
    );
  }

  if (games.length === 0) return null;
  return <GamesPill games={games} count={count ?? games.length} />;
};

export const SegmentedTabs = ({
  tab,
  onChange,
  primaryLabel,
  className,
}: {
  tab: CampaignTab;
  onChange: (t: CampaignTab) => void;
  primaryLabel: string;
  /** Override the default 393px pill width — e.g. `w-full` on mobile. */
  className?: string;
}) => {
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement>(null);
  const items: { id: CampaignTab; label: string }[] = [
    { id: "primary", label: primaryLabel },
    { id: "info", label: t("campaign.inforTabs.info", "Info") },
    { id: "terms", label: t("campaign.inforTabs.termsAndConditions", "T&C") },
  ];

  // The pill follows its OWN urgent copy of the selection and the tab body swaps in a
  // transition: the pill's move is committed and painting before the (much larger) body
  // commit starts, instead of both landing in the same frames. The copy re-syncs when the
  // prop changes from outside (a campaign switch resets to Overview).
  const [active, setActive] = useState(tab);
  const [syncedTab, setSyncedTab] = useState(tab);
  if (tab !== syncedTab) {
    setSyncedTab(tab);
    setActive(tab);
  }
  const select = (id: CampaignTab) => {
    setActive(id);
    startTransition(() => onChange(id));
  };
  const activeIndex = Math.max(
    0,
    items.findIndex((item) => item.id === active)
  );

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = activeIndex;
    const next =
      e.key === "ArrowRight"
        ? (i + 1) % items.length
        : e.key === "ArrowLeft"
          ? (i - 1 + items.length) % items.length
          : e.key === "Home"
            ? 0
            : e.key === "End"
              ? items.length - 1
              : -1;
    if (next < 0) return;
    e.preventDefault();
    select(items[next].id);
    listRef.current
      ?.querySelectorAll<HTMLButtonElement>('[role="tab"]')
      [next]?.focus();
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      onKeyDown={onKeyDown}
      // No backdrop blur: the track sits in the sticky header over content that scrolls under
      // it, and WebKit re-blurs a backdrop on every scrolled frame. The fill is translucent
      // enough on its own over the page colour.
      className={cn(
        "relative flex w-[393px] max-w-full items-start gap-1 overflow-hidden rounded-full p-(--track-pad) [--track-pad:4px] lg:[--track-pad:5px]",
        className
      )}
      style={{ background: TRACK_FILL }}
    >
      <GradientRim background={TRACK_RIM} />
      {/* ONE pill, moved by a CSS transform transition — not a layout animation. The transition
          runs on the compositor, so it keeps gliding while the main thread commits the new tab
          body; a `layoutId` pill is driven from JS and stalled with it. Three equal slots: the
          pill is a third of the track inside its padding, shifted by whole slots plus the gap. */}
      <span
        aria-hidden
        className="pointer-events-none absolute top-(--track-pad) bottom-(--track-pad) left-(--track-pad) w-[calc((100%-2*var(--track-pad)-0.5rem)/3)] rounded-full transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none"
        style={{
          background: PILL_FILL,
          transform: `translateX(calc(${activeIndex} * (100% + 0.25rem)))`,
        }}
      >
        <GradientRim background={PILL_RIM} />
      </span>
      {items.map((item) => {
        const isActive = active === item.id;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => select(item.id)}
            className={cn(
              "relative flex min-h-6 min-w-0 flex-1 items-center justify-center rounded-full py-1 font-heading text-[11px] leading-4 font-semibold transition-colors duration-200 outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background lg:py-[10px] lg:text-[13px]",
              isActive
                ? "text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {/* The label's lift and press are CSS transitions for the same reason as the pill. */}
            <span
              className={cn(
                "relative min-w-0 truncate px-1 transition-transform duration-200 ease-out active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100",
                isActive && "scale-[1.04] motion-reduce:scale-100"
              )}
            >
              {item.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};
